/**
 * Game Session Service - Anti-Cheat Frontend Integration
 *
 * Manages game sessions for anti-cheat validation:
 * - Starts a session when a game begins
 * - Ends a session with score when game ends
 * - Validates scores can be submitted
 */

import { api } from './api.service';

// Types
export interface GameSession {
  sessionId: string;
  gameId: string;
  startTime: number;
  expiresAt: number;
}

export interface SessionEndResult {
  sessionId: string;
  gameId: string;
  playTime: number;
  score: number;
  validationStatus: 'valid' | 'suspicious' | 'invalid';
  validationIssues?: string[];
  canSubmitScore: boolean;
}

// Session storage
let currentSession: GameSession | null = null;

/**
 * Start a new game session
 * Call this when the game actually starts (not just when loading)
 */
export async function startGameSession(
  gameId: string,
  metadata?: Record<string, unknown>
): Promise<GameSession> {
  try {
    const response = await api.post<{
      sessionId: string;
      gameId: string;
      startTime: number;
      expiresAt: number;
    }>('/sessions/start', { gameId, metadata });

    currentSession = response.data;
    return currentSession;
  } catch (error) {
    console.warn('Failed to start game session:', error);
    // Return a local-only session as fallback
    currentSession = {
      sessionId: `local-${Date.now()}`,
      gameId,
      startTime: Date.now(),
      expiresAt: Date.now() + 2 * 60 * 60 * 1000,
    };
    return currentSession;
  }
}

/**
 * End the current game session
 * Call this when the game ends with the final score
 */
export async function endGameSession(
  score: number,
  metadata?: Record<string, unknown>
): Promise<SessionEndResult | null> {
  if (!currentSession) {
    console.warn('No active session to end');
    return null;
  }

  // Skip API call for local-only sessions
  if (currentSession.sessionId.startsWith('local-')) {
    const playTime = (Date.now() - currentSession.startTime) / 1000;
    const result: SessionEndResult = {
      sessionId: currentSession.sessionId,
      gameId: currentSession.gameId,
      playTime,
      score,
      validationStatus: 'valid',
      canSubmitScore: true,
    };
    currentSession = null;
    return result;
  }

  try {
    const response = await api.post<SessionEndResult>('/sessions/end', {
      sessionId: currentSession.sessionId,
      score,
      metadata,
    });

    currentSession = null;
    return response.data;
  } catch (error) {
    console.warn('Failed to end game session:', error);
    currentSession = null;
    return null;
  }
}

/**
 * Get the current active session
 */
export function getCurrentSession(): GameSession | null {
  // Check if session has expired
  if (currentSession && currentSession.expiresAt < Date.now()) {
    currentSession = null;
  }
  return currentSession;
}

/**
 * Get the current session ID (for score submission)
 */
export function getSessionId(): string | null {
  return currentSession?.sessionId || null;
}

/**
 * Check if there's an active session
 */
export function hasActiveSession(): boolean {
  return getCurrentSession() !== null;
}

/**
 * Clear the current session (e.g., when navigating away)
 */
export function clearSession(): void {
  currentSession = null;
}

/**
 * Hook for React components to manage game sessions
 */
export function useGameSession() {
  return {
    startSession: startGameSession,
    endSession: endGameSession,
    getSession: getCurrentSession,
    getSessionId,
    hasActiveSession,
    clearSession,
  };
}

export const sessionService = {
  start: startGameSession,
  end: endGameSession,
  getCurrent: getCurrentSession,
  getSessionId,
  hasActive: hasActiveSession,
  clear: clearSession,
};

export default sessionService;
